import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Ticket, ChevronDown } from 'lucide-react';
import { PickRow, legText } from './SuggestionsTab';
import WhySafe from './WhySafe';
import { useBetMode, winFor } from '../lib/betMode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;
const pct = (p) => `${Math.round(p * 100)}%`;

export const MlRow = ({ x, testId }) => {
  const mode = useBetMode();
  return (
  <div className="flex items-start gap-2 py-1.5 border-b border-zinc-800/70 last:border-0" data-testid={testId}>
    {x.team_logo && <img src={x.team_logo} alt="" className="w-5 h-5 rounded-full bg-white/90 p-[1px] object-contain shrink-0" />}
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-white leading-snug" data-testid={`${testId}-text`}>{winFor(mode, x)}</p>
      {mode !== 'pro' && <p className="text-[11px] text-zinc-400">They just need to win — the final score margin doesn't matter.</p>}
      <p className="text-[11px] text-zinc-500">{x.game}{{ nba: ' · NBA', nfl: ' · NFL' }[x.league] || ' · College'}</p>
      <p className="text-[11px] text-amber-200/80 italic">{x.reason}</p>
      <WhySafe lines={x.why} testId={`${testId}-why`} label={x.prob >= 0.65 ? "Why it's safe" : 'Why this pick'} />
    </div>
    <span className="shrink-0 rounded-md border border-emerald-500/40 bg-emerald-500/20 px-1.5 py-0.5 font-mono text-xs font-bold text-emerald-300">{pct(x.prob)}{mode === 'pro' ? '' : ' likely'}</span>
  </div>
  );
};

const RATING_TONE = { LOCK: 'bg-emerald-500 text-black', 'VERY SECURE': 'bg-sky-500 text-black', SECURE: 'bg-zinc-600 text-white',
  HOT: 'bg-red-500 text-white', WARM: 'bg-orange-400 text-black', COLD: 'bg-sky-300 text-black' };
const RATING_LABEL = { HOT: '🔥 HOT', WARM: 'WARM', COLD: '❄ COLD' };

export const SafeTicket = ({ ticket, n, league, trackable = true, amount }) => {
  const { legs } = ticket;
  const [open, setOpen] = useState(false);
  const track = async (e) => {
    e.stopPropagation();
    const out = legs.map((x, i) => (x.kind === 'prop'
      ? { id: `s${i}`, league: league === 'nba' ? 'nba' : 'nfl', bet_type: 'player_prop', player_name: x.player, stat_target: x.stat, selection: legText(x), line: `${x.mark}+` }
      : { id: `s${i}`, league: x.league || 'cfb', bet_type: 'moneyline', selection: x.team }));
    try {
      await axios.post(`${API}/parlays`, { legs: out, leg_count: out.length, descriptor: `AI Safe ${n} (${league.toUpperCase()})` });
      toast.success(`Safe ${n} added to your tracker`);
    } catch (err) { toast.error(`Could not add: ${err.response?.data?.detail || err.message}`); }
  };
  const id = `${league}-${n}`;
  return (
    <div className="rounded-lg border border-emerald-500/30 bg-zinc-900/60" data-testid={`safe-ticket-${id}`}>
      <div role="button" tabIndex={0} onClick={() => setOpen((o) => !o)} onKeyDown={(e) => e.key === 'Enter' && setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2.5 cursor-pointer" data-testid={`safe-ticket-toggle-${id}`}>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        <p className="flex-1 min-w-0 text-sm font-semibold text-white">
          Safe {n} <span className={`ml-1 rounded px-1.5 py-0.5 text-[9px] font-bold align-middle ${RATING_TONE[ticket.rating]}`} data-testid={`safe-ticket-rating-${id}`}>{RATING_LABEL[ticket.rating] || ticket.rating}</span>
          <span className="block text-[11px] text-zinc-400 font-normal">{legs.length} legs · avg {pct(ticket.avg)} · all-hit chance {pct(ticket.all_hit)}{amount ? <b className="ml-1 text-amber-300" data-testid={`safe-ticket-bet-${id}`}>· Bet ${amount}</b> : null}</span>
        </p>
        {trackable && <button type="button" onClick={track} className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-2.5 py-1 text-[11px] font-bold text-black hover:bg-amber-400" data-testid={`safe-ticket-track-${id}`}>
          <Ticket className="w-3.5 h-3.5" />Track
        </button>}
      </div>
      {open && (
        <div className="px-3 pb-2" data-testid={`safe-ticket-legs-${id}`}>
          {legs.map((x, i) => (x.kind === 'prop'
            ? <PickRow key={`${x.event_id}-${x.player}-${x.stat}`} x={x} testId={`safe-leg-${id}-${i}`} />
            : <MlRow key={x.event_id} x={x} testId={`safe-leg-${id}-${i}`} />))}
        </div>
      )}
    </div>
  );
};

export const SafeSection = ({ title, tickets, league, trackable = true, amount, children }) => (
  <div className="rounded-xl border border-emerald-500/40 bg-emerald-950/20 p-3 space-y-2" data-testid={`safe-section-${league}`}>
    <p className="text-sm font-bold text-emerald-300 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" />{title}</p>
    {tickets.length > 0 && <p className="text-[10px] text-zinc-500">{tickets.length} ticket{tickets.length > 1 ? 's' : ''}, no pick repeated, max 2 per game, ranked most → least secure{amount ? ` · total $${(amount * tickets.length).toFixed(2)} if you play all` : ''}</p>}
    {tickets.length
      ? tickets.map((t, i) => <SafeTicket key={i} ticket={t} n={i + 1} league={league} trackable={trackable} amount={amount} />)
      : <p className="text-xs text-zinc-500" data-testid={`safe-section-empty-${league}`}>Not enough high-confidence legs yet for a 5–8 leg safe ticket.</p>}
    {children}
  </div>
);

